import KRGlue from "@lyracom/embedded-form-glue";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import PhoneInput from "react-phone-number-input";
import "react-phone-number-input/style.css";
import { useNavigate } from "react-router-dom";
import { useConfig } from "../../config/ConfigContext";
import { BASE_URL, N8N_ORDER_CONFIRMATION_URL } from "../../constants";
import {
  getCheckoutRequiredFieldNames,
  isCheckoutFieldRequired,
  isDeliveryDateReadOnly,
  isPartnerCheckout,
  shouldShowBillingSection,
  shouldShowRentalAgreement,
} from "../../utils/checkoutFormConfig";
import { computeOrderTotals } from "../../utils/orderPricing";
import { serializeCartForOrder } from "../../utils/serializeCartForOrder";
import { useToys } from "../Toy/ToysContext";
import CheckoutTimePicker from "./CheckoutTimePicker";
import OrderSummaryCheckout from "./OrderSummaryCheckout";
import PaymentModal from "./PaymentModal";
import { getRentalAgreementDraftHtml } from "./rentalAgreementDraftHtml";

function Checkout() {
  const { cart, reservationDate } = useToys();
  const config = useConfig();
  const agreementDoneDate = useMemo(
    () =>
      new Date().toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      }),
    [],
  );
  const noCheckout = config.noCheckout || config.id === "versionNoCheckout";
  const isPartner = isPartnerCheckout(config);
  const isPyV3 = config.id === "version3";
  const showRentalAgreement = shouldShowRentalAgreement(config);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [contractAccepted, setContractAccepted] = useState(false);
  const [companyCheck, setCompanyCheck] = useState(false);
  const [contractModalOpen, setContractModalOpen] = useState(false);
  const [contractErrorVisible, setContractErrorVisible] = useState(false);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [paymentFormLoading, setPaymentFormLoading] = useState(!noCheckout);
  const [paymentFormReady, setPaymentFormReady] = useState(false);
  const [paymentSetupError, setPaymentSetupError] = useState(null);
  const [paymentSubmitError, setPaymentSubmitError] = useState(null);
  const baseUrl = BASE_URL;
  const paymentSetupSeqRef = useRef(0);

  const [form, setForm] = useState({
    deliveryDate: null,
    latestDeliveryTime: null,
    boatName: null,
    boatModel: null,
    recipientName: null,
    recipientPhone: null,
    googleMapsLink: null,
    deliveryNotes: null,
    email: null,
    firstName: null,
    lastName: null,
    companyName: null,
    companyRegistration: null,
    address1: null,
    postcode: null,
    city: null,
    country: null,
    numberOfPeople: null,
  });

  const formRef = useRef({});
  useEffect(() => {
    formRef.current = form;
  }, [form]);
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  useEffect(() => {
    if (reservationDate) {
      setForm((prev) => ({
        ...prev,
        deliveryDate: new Date(reservationDate),
      }));
    }
  }, [reservationDate]);

  const handleChange = (e) => {
    const { name, value: inputValue } = e.target;
    setForm((prevForm) => ({
      ...prevForm,
      [name]: inputValue,
    }));
    setErrors((prevErrors) => {
      const updatedErrors = { ...prevErrors };
      if (inputValue.trim() !== "") {
        delete updatedErrors[name];
      }
      return updatedErrors;
    });
  };

  const handleRecipientPhone = (value) => {
    setForm((prev) => ({ ...prev, recipientPhone: value ?? "" }));
    setErrors((prevErrors) => {
      const next = { ...prevErrors };
      delete next.recipientPhone;
      return next;
    });
  };

  const isValidEmail = (email) => {
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return regex.test(email);
  };

  const totalItemPriceTTC = cart.reduce(
    (sum, item) =>
      sum + item.quantity * item.toy.fields[config.itemPriceFieldTTC],
    0,
  );

  const {
    deliveryFee,
    totalDiscount,
    totalPriceDisplay,
    totalPriceHT,
    partnerCommissionPercent,
  } = computeOrderTotals(config, totalItemPriceTTC);

  /** Shared n8n order fields (stable ref for hooks deps). */
  const orderConfirmationTotals = useMemo(
    () => ({
      totalPriceDisplay,
      totalPriceHT,
      totalDiscount,
      deliveryFee,
      totalItemPriceTTC,
      partnerCommissionPercent,
    }),
    [
      totalPriceDisplay,
      totalPriceHT,
      totalDiscount,
      deliveryFee,
      totalItemPriceTTC,
      partnerCommissionPercent,
    ],
  );

  const buildOrderSnapshot = useCallback(
    () => ({
      project: "toys-request",
      partnerId: config.id,
      formData: formRef.current,
      cart: serializeCartForOrder(cart, config),
      totals: orderConfirmationTotals,
      ...(reservationDate != null ? { reservationDate } : {}),
    }),
    [cart, config, orderConfirmationTotals, reservationDate],
  );

  const handlePaymentSubmit = useCallback(
    async (paymentData) => {
      setPaymentSubmitError(null);
      try {
        const response = await fetch(
          `${baseUrl}/.netlify/functions/acceptPayment`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              ...paymentData,
              orderSnapshot: buildOrderSnapshot(),
            }),
          },
        );

        let paymentResult;
        try {
          paymentResult = await response.json();
        } catch {
          setPaymentSubmitError(
            "Invalid response from the payment server. Please try again.",
          );
          return;
        }

        if (
          response.status === 200 &&
          paymentResult.message === "Valid payment"
        ) {
          const orderResponse = await fetch(N8N_ORDER_CONFIRMATION_URL, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              formData: formRef.current,
              paymentId:
                paymentResult.transactionId ?? paymentData.transactionId,
              cart: serializeCartForOrder(cart, config),
              config,
              ...orderConfirmationTotals,
            }),
          });

          let orderResponseData;
          try {
            orderResponseData = await orderResponse.json();
          } catch {
            setPaymentSubmitError(
              "Order could not be confirmed. If you were charged, please contact us with your payment reference.",
            );
            return;
          }

          if (orderResponseData.success) {
            const fd = formRef.current;
            const nameState = isPartner
              ? {
                  firstName: fd.recipientName?.trim() || "",
                  lastName: "",
                }
              : {
                  firstName: fd.firstName,
                  lastName: fd.lastName,
                };
            navigate("/confirmation", {
              state: {
                orderNumber: orderResponseData.orderNumber,
                ...nameState,
              },
            });
          } else {
            console.error("⚠ Order storage failed");
            setPaymentSubmitError(
              typeof orderResponseData?.message === "string"
                ? orderResponseData.message
                : "Your payment may have succeeded but the order could not be saved. Please contact support.",
            );
          }
        } else {
          console.error("⚠ Payment validation failed");
          const msg =
            typeof paymentResult?.message === "string"
              ? paymentResult.message
              : typeof paymentResult?.error === "string"
                ? paymentResult.error
                : null;
          setPaymentSubmitError(
            msg ||
              "Payment was declined or could not be verified. Please check your card details and try again.",
          );
        }
      } catch (error) {
        console.error("❌ Error processing payment:", error);
        setPaymentSubmitError(
          "A network error occurred. Please check your connection and try again.",
        );
      }
    },
    [baseUrl, navigate, cart, config, orderConfirmationTotals, isPartner, buildOrderSnapshot],
  );

  useEffect(() => {
    if (noCheckout) {
      setPaymentFormLoading(false);
      setPaymentFormReady(false);
      setPaymentSetupError(null);
      return;
    }

    const seq = ++paymentSetupSeqRef.current;
    let cancelled = false;

    const isStale = () =>
      cancelled || seq !== paymentSetupSeqRef.current;

    async function setupPaymentForm() {
      setPaymentFormLoading(true);
      setPaymentFormReady(false);
      setPaymentSetupError(null);

      try {
        const endpoint = "https://api.systempay.fr";
        const publicKey = process.env.REACT_APP_SYSTEM_PAY_PUBLIC_KEY;

        if (!publicKey) {
          if (!isStale()) {
            setPaymentSetupError(
              "Payment is not configured. Please contact the site administrator.",
            );
          }
          return;
        }

        const response = await fetch(
          `${baseUrl}/.netlify/functions/createFormToken`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              paymentConf: {
                amount: Math.round(totalPriceDisplay * 100),
                currency: "EUR",
              },
              orderSnapshot: buildOrderSnapshot(),
            }),
          },
        );
        if (isStale()) return;

        let data;
        try {
          data = await response.json();
        } catch {
          if (!isStale()) {
            setPaymentSetupError(
              "Invalid response while starting payment. Please refresh the page.",
            );
          }
          return;
        }
        if (isStale()) return;

        if (!response.ok) {
          if (!isStale()) {
            const msg =
              typeof data?.message === "string"
                ? data.message
                : typeof data?.error === "string"
                  ? data.error
                  : null;
            setPaymentSetupError(
              msg || "Could not start the payment session. Please try again.",
            );
          }
          return;
        }

        if (!data?.formToken) {
          if (!isStale()) {
            setPaymentSetupError(
              "Could not obtain a secure payment session. Please refresh the page.",
            );
          }
          return;
        }

        const { KR } = await KRGlue.loadLibrary(
          endpoint,
          publicKey,
          data.formToken,
        );
        if (isStale()) return;

        await KR.setFormConfig({
          "kr-language": "en-US",
          "kr-popin": true,
        });
        if (isStale()) return;

        await KR.setFormToken(data.formToken);
        if (isStale()) return;

        await KR.removeForms();
        if (isStale()) return;

        await KR.renderElements("#myPaymentForm");
        if (isStale()) return;

        await KR.onSubmit(async (paymentData) => {
          await handlePaymentSubmit(paymentData);
        });

        if (!isStale()) {
          setPaymentFormReady(true);
          setPaymentSetupError(null);
        }
      } catch (error) {
        console.error("Payment form setup error:", error);
        if (!isStale()) {
          setPaymentSetupError(
            "The payment form could not be loaded. Please refresh the page or try again later.",
          );
        }
      } finally {
        if (!isStale()) {
          setPaymentFormLoading(false);
        }
      }
    }

    setupPaymentForm();
    return () => {
      cancelled = true;
    };
  }, [noCheckout, baseUrl, handlePaymentSubmit, totalPriceDisplay, buildOrderSnapshot]);

  const getValidationErrors = () => {
    const newErrors = {};
    const merged = getCheckoutRequiredFieldNames(config, { companyCheck });
    merged.forEach((field) => {
      const value = form[field];
      if (field === "deliveryDate" && !(value instanceof Date)) {
        newErrors[field] = true;
      } else if (
        value === null ||
        value === undefined ||
        (typeof value === "string" && value.trim() === "")
      ) {
        newErrors[field] = true;
      }
    });
    if (form.email && !isValidEmail(form.email)) {
      newErrors.email = true;
    }
    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (showRentalAgreement && !contractAccepted) {
      setContractErrorVisible(true);
      return;
    }
    setContractErrorVisible(false);

    const validationErrors = getValidationErrors();

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    if (!noCheckout) {
      if (paymentFormLoading) {
        setPaymentSetupError(
          "Payment is still loading. Please wait a few seconds and try again.",
        );
        return;
      }
      if (!paymentFormReady) {
        setPaymentSetupError(
          paymentSetupError ||
            "Payment is not available right now. Please refresh the page or try again later.",
        );
        return;
      }
    }

    if (noCheckout) {
      setIsSubmitting(true);
      setSubmitError(null);
      try {
        const orderResponse = await fetch(N8N_ORDER_CONFIRMATION_URL, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            formData: formRef.current,
            cart: serializeCartForOrder(cart, config),
            config,
            ...orderConfirmationTotals,
          }),
        });

        const orderResponseData = await orderResponse.json();

        if (orderResponseData.success) {
          const fd = formRef.current;
          const nameState = isPartner
            ? {
                firstName: fd.recipientName?.trim() || "",
                lastName: "",
              }
            : {
                firstName: fd.firstName,
                lastName: fd.lastName,
              };
          navigate("/confirmation", {
            state: {
              orderNumber: orderResponseData.orderNumber,
              ...nameState,
              noPayment: true,
            },
          });
        } else {
          setSubmitError("Order submission failed. Please try again.");
        }
      } catch (error) {
        console.error("Error submitting order:", error);
        setSubmitError("An error occurred. Please try again.");
      } finally {
        setIsSubmitting(false);
      }
      return;
    }

    setPaymentSubmitError(null);
    setIsModalOpen(true);
    document.getElementById("myPaymentForm")?.classList.remove("hidden");
    document
      .querySelector(".kr-card-holder-mail input")
      ?.setAttribute("value", form.email);
  };

  const fieldClass = (field) =>
    `field-co ${errors[field] ? "input-error" : ""}`.trim();

  useEffect(() => {
    if (cart.length === 0) {
      navigate("/", { replace: true });
    }
  }, [cart.length, navigate]);

  if (cart.length === 0) {
    return null;
  }

  const deliveryFmt =
    form.deliveryDate instanceof Date
      ? form.deliveryDate.toLocaleDateString("en-GB", {
          weekday: "long",
          day: "numeric",
          month: "long",
          year: "numeric",
        })
      : "";

  const req = (fieldKey) =>
    isCheckoutFieldRequired(config, fieldKey, { companyCheck });

  const openContract = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setContractModalOpen(true);
  };

  const acceptContract = () => {
    setContractAccepted(true);
    setContractErrorVisible(false);
    setContractModalOpen(false);
  };

  return (
    <div className="app-shell page-checkout">
      <div className="checkout-top">
        <button
          type="button"
          className="back-btn"
          onClick={() => navigate("/")}
        >
          ← Back to selection
        </button>
      </div>

      <div className="co-wrap">
        <h1 className="co-title">Confirm and Pay</h1>
        <div className="co-form-col">
          <form onSubmit={handleSubmit} noValidate>
            <div className="form-sec">
              <div className="form-sec-title">Delivery Information</div>

              {isDeliveryDateReadOnly(config) ? (
                <div className="fg one">
                  <div className="field field-co">
                    <label htmlFor="deliveryDateDisplay">
                      Delivery Date{req("deliveryDate") ? " *" : ""}
                    </label>
                    <input
                      id="deliveryDateDisplay"
                      readOnly
                      tabIndex={-1}
                      className="input-style w-full co-readonly-date"
                      value={deliveryFmt || "—"}
                    />
                  </div>
                </div>
              ) : null}

              {isPartner ? (
                <>
                  <div className="fg one">
                    <div className="field field-co">
                      <label htmlFor="coLatestDeliveryTime">
                        Latest Delivery Time
                        {req("latestDeliveryTime") ? " *" : ""}
                      </label>
                      <CheckoutTimePicker
                        id="coLatestDeliveryTime"
                        value={form.latestDeliveryTime ?? ""}
                        onChange={(v) => {
                          setForm((p) => ({
                            ...p,
                            latestDeliveryTime: v,
                          }));
                          setErrors((prev) => {
                            const n = { ...prev };
                            delete n.latestDeliveryTime;
                            return n;
                          });
                        }}
                        hasError={Boolean(errors.latestDeliveryTime)}
                      />
                    </div>
                  </div>
                  <div className="fg">
                    <div className="field field-co">
                      <label htmlFor="email">
                        Email{req("email") ? " *" : ""}
                      </label>
                      <input
                        id="email"
                        type="email"
                        name="email"
                        placeholder="contact@example.com"
                        value={form.email ?? ""}
                        onChange={handleChange}
                        className={fieldClass("email")}
                      />
                    </div>
                    <div className="field field-co">
                      <label htmlFor="googleMapsLink">
                        Google Maps Link{req("googleMapsLink") ? " *" : ""}
                      </label>
                      <input
                        id="googleMapsLink"
                        type="text"
                        name="googleMapsLink"
                        placeholder="Link to pin or map"
                        value={form.googleMapsLink ?? ""}
                        onChange={handleChange}
                        className={fieldClass("googleMapsLink")}
                      />
                    </div>
                  </div>
                  <div className="fg">
                    <div className="field field-co">
                      <label htmlFor="boatName">Boat Name</label>
                      <input
                        id="boatName"
                        type="text"
                        name="boatName"
                        placeholder="e.g. Lady M"
                        value={form.boatName ?? ""}
                        onChange={handleChange}
                        className={fieldClass("boatName")}
                      />
                    </div>
                    <div className="field field-co">
                      <label htmlFor="boatModel">Boat Model</label>
                      <input
                        id="boatModel"
                        type="text"
                        name="boatModel"
                        placeholder="Model"
                        value={form.boatModel ?? ""}
                        onChange={handleChange}
                        className={fieldClass("boatModel")}
                      />
                    </div>
                  </div>
                  <div className="fg">
                    <div className="field field-co">
                      <label htmlFor="recipientName">Recipient Name</label>
                      <input
                        id="recipientName"
                        type="text"
                        name="recipientName"
                        placeholder="Full name"
                        value={form.recipientName ?? ""}
                        onChange={handleChange}
                        className={fieldClass("recipientName")}
                      />
                    </div>
                    <div className="field field-co">
                      <label htmlFor="recipientPhone">Recipient Phone</label>
                      <PhoneInput
                        id="recipientPhone"
                        international
                        defaultCountry="FR"
                        value={form.recipientPhone ?? ""}
                        onChange={handleRecipientPhone}
                        className="phone-input-co"
                      />
                    </div>
                  </div>
                  <div className="fg one">
                    <div className="field field-co">
                      <label htmlFor="deliveryNotes">Notes</label>
                      <textarea
                        id="deliveryNotes"
                        name="deliveryNotes"
                        placeholder="Special requests, delivery location, timing…"
                        value={form.deliveryNotes ?? ""}
                        onChange={handleChange}
                        className={fieldClass("deliveryNotes")}
                      />
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <div className="fg one">
                    <div className="field field-co">
                      <label htmlFor="boatName">Boat Name</label>
                      <input
                        id="boatName"
                        type="text"
                        name="boatName"
                        placeholder="e.g. Lady M"
                        value={form.boatName ?? ""}
                        onChange={handleChange}
                        className={fieldClass("boatName")}
                      />
                    </div>
                  </div>
                  <div className="fg">
                    <div className="field field-co">
                      <label htmlFor="firstName">
                        First Name{req("firstName") ? " *" : ""}
                      </label>
                      <input
                        id="firstName"
                        type="text"
                        name="firstName"
                        placeholder="John"
                        value={form.firstName ?? ""}
                        onChange={handleChange}
                        className={fieldClass("firstName")}
                      />
                    </div>
                    <div className="field field-co">
                      <label htmlFor="lastName">
                        Last Name{req("lastName") ? " *" : ""}
                      </label>
                      <input
                        id="lastName"
                        type="text"
                        name="lastName"
                        placeholder="Doe"
                        value={form.lastName ?? ""}
                        onChange={handleChange}
                        className={fieldClass("lastName")}
                      />
                    </div>
                  </div>
                  <div className="fg">
                    <div className="field field-co">
                      <label htmlFor="email">
                        Email{req("email") ? " *" : ""}
                      </label>
                      <input
                        id="email"
                        type="email"
                        name="email"
                        placeholder="john@example.com"
                        value={form.email ?? ""}
                        onChange={handleChange}
                        className={fieldClass("email")}
                      />
                    </div>
                    <div className="field field-co">
                      <label htmlFor="numberOfPeople">Number of People</label>
                      <input
                        id="numberOfPeople"
                        type="number"
                        name="numberOfPeople"
                        min={1}
                        placeholder="6"
                        value={form.numberOfPeople ?? ""}
                        onChange={handleChange}
                        className={fieldClass("numberOfPeople")}
                      />
                    </div>
                  </div>
                  <div className="fg one">
                    <div className="field field-co">
                      <label htmlFor="deliveryNotes">Notes</label>
                      <textarea
                        id="deliveryNotes"
                        name="deliveryNotes"
                        placeholder="Special requests, delivery location, timing…"
                        value={form.deliveryNotes ?? ""}
                        onChange={handleChange}
                        className={fieldClass("deliveryNotes")}
                      />
                    </div>
                  </div>
                </>
              )}
            </div>

            {shouldShowBillingSection(config) ? (
              <div className="form-sec" style={{ marginTop: 8 }}>
                <div className="form-sec-title">Billing Information</div>
                <div className="fg one">
                  <div className="field field-co">
                    <label htmlFor="address1">
                      Address{req("address1") ? " *" : ""}
                    </label>
                    <input
                      id="address1"
                      type="text"
                      name="address1"
                      placeholder="123 Boulevard de la Croisette"
                      value={form.address1 ?? ""}
                      onChange={handleChange}
                      className={fieldClass("address1")}
                    />
                  </div>
                </div>
                <div className="fg">
                  <div className="field field-co">
                    <label htmlFor="postcode">
                      Postal Code{req("postcode") ? " *" : ""}
                    </label>
                    <input
                      id="postcode"
                      type="text"
                      name="postcode"
                      placeholder="06400"
                      value={form.postcode ?? ""}
                      onChange={handleChange}
                      className={fieldClass("postcode")}
                    />
                  </div>
                  <div className="field field-co">
                    <label htmlFor="city">
                      City{req("city") ? " *" : ""}
                    </label>
                    <input
                      id="city"
                      type="text"
                      name="city"
                      placeholder="Cannes"
                      value={form.city ?? ""}
                      onChange={handleChange}
                      className={fieldClass("city")}
                    />
                  </div>
                </div>
                <div className="fg one">
                  <div className="field field-co">
                    <label htmlFor="country">
                      Country{req("country") ? " *" : ""}
                    </label>
                    <input
                      id="country"
                      type="text"
                      name="country"
                      placeholder="France"
                      value={form.country ?? ""}
                      onChange={handleChange}
                      className={fieldClass("country")}
                    />
                  </div>
                </div>

                <label
                  className={`company-toggle${companyCheck ? " checked" : ""}`}
                  id="companyToggle"
                >
                  <div className="company-toggle-box">
                    <input
                      type="checkbox"
                      id="companyCheck"
                      checked={companyCheck}
                      onChange={(e) => {
                        const on = e.target.checked;
                        setCompanyCheck(on);
                        if (!on) {
                          setForm((prev) => ({
                            ...prev,
                            companyName: "",
                            companyRegistration: "",
                          }));
                          setErrors((prev) => {
                            const next = { ...prev };
                            delete next.companyName;
                            delete next.companyRegistration;
                            return next;
                          });
                        }
                      }}
                    />
                    <div className="company-toggle-icon" id="companyIcon">
                      🏢
                    </div>
                    <div className="company-toggle-text">
                      <div className="company-toggle-title">
                        {isPyV3
                          ? "I am a company"
                          : "I am ordering for a company"}
                      </div>
                      <div className="company-toggle-sub">
                        Add your company name and registration number for the
                        invoice
                      </div>
                    </div>
                    <div className="company-toggle-chevron" id="companyChevron">
                      ›
                    </div>
                  </div>
                </label>

                <div
                  className={`company-fields${companyCheck ? " open" : ""}`}
                  id="companyFields"
                >
                  <div className="fg">
                    <div className="field field-co">
                      <label htmlFor="companyName">
                        Company Name{req("companyName") ? " *" : ""}
                      </label>
                      <input
                        id="companyName"
                        type="text"
                        name="companyName"
                        placeholder="Acme Ltd."
                        value={form.companyName ?? ""}
                        onChange={handleChange}
                        className={fieldClass("companyName")}
                      />
                    </div>
                    <div className="field field-co">
                      <label htmlFor="companyRegistration">
                        Registration Number
                        {req("companyRegistration") ? " *" : ""}
                      </label>
                      <input
                        id="companyRegistration"
                        type="text"
                        name="companyRegistration"
                        placeholder="e.g. SIREN / VAT ID"
                        value={form.companyRegistration ?? ""}
                        onChange={handleChange}
                        className={fieldClass("companyRegistration")}
                      />
                    </div>
                  </div>
                </div>
              </div>
            ) : null}

            {showRentalAgreement ? (
              <>
                <label
                  className="contract-row"
                  id="contractRow"
                  htmlFor="contractCheck"
                >
                  <input
                    type="checkbox"
                    id="contractCheck"
                    checked={contractAccepted}
                    onChange={() => {
                      setContractAccepted((v) => !v);
                      setContractErrorVisible(false);
                    }}
                  />
                  <span className="contract-row-text">
                    I have read and I accept the{" "}
                    <button
                      type="button"
                      className="contract-link"
                      onClick={openContract}
                    >
                      Rental Agreement
                    </button>
                    . Once payment is processed, I will receive the signed
                    contract by email.{" "}
                  </span>
                </label>

                <div
                  className={`contract-error${contractErrorVisible ? " show" : ""}`}
                  id="contractError"
                >
                  Please accept the Rental Agreement before proceeding to
                  payment.
                </div>
              </>
            ) : null}

            {Object.keys(errors).length > 0 && (
              <p className="text-sm text-red-500 mt-2">
                Please fill in all required fields.
              </p>
            )}
            {submitError && (
              <p className="text-sm text-red-500">{submitError}</p>
            )}

            {!noCheckout && paymentSetupError && (
              <div className="contract-error show co-payment-err" role="alert">
                {paymentSetupError}
              </div>
            )}

            <button
              type="submit"
              className="confirm-btn"
              disabled={
                (showRentalAgreement && !contractAccepted) ||
                (noCheckout && isSubmitting) ||
                (!noCheckout &&
                  (paymentFormLoading || !paymentFormReady))
              }
            >
              {noCheckout
                ? isSubmitting
                  ? "Submitting..."
                  : "Submit Order"
                : paymentFormLoading
                  ? "Preparing payment…"
                  : "Confirm and Pay"}
            </button>
          </form>
        </div>

        <div className="co-summary-aside">
          <OrderSummaryCheckout
            cart={cart}
            config={config}
            deliveryFee={deliveryFee}
            totalDiscount={totalDiscount}
            totalPriceDisplay={totalPriceDisplay}
            totalPriceHT={totalPriceHT}
            noCheckout={noCheckout}
            summaryDateLabel={deliveryFmt}
          />
        </div>
      </div>

      <div
        className={`contract-modal-bg${contractModalOpen ? " open" : ""}`}
        onClick={(e) => {
          if (e.target === e.currentTarget) setContractModalOpen(false);
        }}
        role="presentation"
      >
        <div
          className="contract-modal"
          onClick={(e) => e.stopPropagation()}
          role="dialog"
          aria-modal="true"
          aria-labelledby="contract-modal-title"
        >
          <div className="contract-modal-head">
            <h2 className="contract-modal-title" id="contract-modal-title">
              Rental Agreement{" "}
              <em className="contract-modal-title-em">— Draft</em>
            </h2>
            <button
              type="button"
              className="contract-modal-close"
              onClick={() => setContractModalOpen(false)}
              aria-label="Close"
            >
              ×
            </button>
          </div>
          <div
            className="contract-modal-body contract-draft-legal"
            dangerouslySetInnerHTML={{
              __html: getRentalAgreementDraftHtml(agreementDoneDate),
            }}
          />
          <div className="contract-modal-foot">
            <p className="contract-modal-note">
              This is a draft. The final signed contract will be sent to your
              email after payment.
            </p>
            <button
              type="button"
              className="contract-accept-btn"
              onClick={acceptContract}
            >
              Accept &amp; Close
            </button>
          </div>
        </div>
      </div>

      {!noCheckout && (
        <PaymentModal
          isOpen={isModalOpen}
          onClose={() => {
            setIsModalOpen(false);
            setPaymentSubmitError(null);
          }}
          title="Payment Information"
        >
          {paymentSubmitError ? (
            <div
              className="contract-error show co-payment-err co-payment-err--modal"
              role="alert"
            >
              {paymentSubmitError}
            </div>
          ) : null}
          <div id="myPaymentForm" className={isModalOpen ? "" : "hidden"}>
            <div className="kr-smart-form">
              <div className="kr-embedded">
                <div className="kr-cardholder-name" />
                <div className="kr-pan" />
                <div className="kr-security-code w-1/2" />
                <div className="kr-expiry w-1/2" />
              </div>
            </div>
          </div>
        </PaymentModal>
      )}
    </div>
  );
}

export default Checkout;
